import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { CheckCircle2, ChevronDown, ChevronRight, ChevronUp, Circle } from "lucide-react";
import type { SetupItem } from "@shared/onboarding";

/** GET /api/workspaces/:wsId/setup-status (server/onboarding.ts getSetupStatus) */
interface SetupStatus {
  shown: boolean;
  items: SetupItem[];
  doneCount: number;
  total: number;
  allDone: boolean;
}

const collapseKey = (workspaceId: number) => `drpr-finish-setup-collapsed-${workspaceId}`;

/** localStorage is a per-viewer convenience only: it can be empty, blocked or throw, and the card must work without it. */
function readCollapsed(workspaceId: number): boolean {
  try {
    const v = localStorage.getItem(collapseKey(workspaceId));
    if (v !== null) return v === "1";
  } catch { /* fall through to the default */ }
  // Phones start folded (one line) so the chat stays in view
  return typeof window !== "undefined" && window.innerWidth < 640;
}
function writeCollapsed(workspaceId: number, v: boolean): void {
  try { localStorage.setItem(collapseKey(workspaceId), v ? "1" : "0"); } catch { /* blocked: the default next time */ }
}

/**
 * Draper home, after the onboarding chat: what is left to set up, from the real state (connected channels, WordPress, booking page,
 * website chat, email sender + CASL footer, the business knowledge page). Each line opens the exact place to do it; the card is
 * hidden once everything is done (and for workspaces that never went through the onboarding chat).
 */
export function FinishSetupCard({ workspaceId }: { workspaceId: number | null }) {
  const [, navigate] = useLocation();
  const [collapsed, setCollapsed] = useState(() => (workspaceId ? readCollapsed(workspaceId) : false));
  const { data } = useQuery<SetupStatus | null>({
    queryKey: ["/api/workspaces", workspaceId, "setup-status"],
    queryFn: async () => {
      if (!workspaceId) return null;
      const res = await fetch(`/api/workspaces/${workspaceId}/setup-status`, { credentials: "include" });
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!workspaceId,
    staleTime: 0, // re-read on every visit: the owner usually comes back from the settings page they were sent to
    refetchOnWindowFocus: true,
  });

  if (!workspaceId || !data || !data.shown || data.allDone) return null;
  const pct = Math.round((data.doneCount / Math.max(1, data.total)) * 100);
  const toggle = () => { const next = !collapsed; setCollapsed(next); writeCollapsed(workspaceId, next); };

  return (
    <div className="mx-6 mt-4 rounded-2xl border p-4" style={{ background: "rgba(255,255,255,0.03)", borderColor: "rgba(255,255,255,0.08)" }} data-testid="finish-setup-card">
      <button onClick={toggle} className="w-full flex items-center justify-between gap-3 text-left" data-testid="button-toggle-finish-setup" aria-expanded={!collapsed}>
        <span className="text-xs font-semibold text-white/60 tracking-wide">FINISH SETUP</span>
        <span className="flex items-center gap-2 text-xs font-bold text-white/40">
          {data.doneCount}/{data.total}
          {collapsed ? <ChevronDown size={13} /> : <ChevronUp size={13} />}
        </span>
      </button>
      <div className="w-full h-1 rounded-full mt-3" style={{ background: "rgba(255,255,255,0.08)" }}>
        <div className="h-1 rounded-full bg-blue-500/70 transition-all" style={{ width: `${pct}%` }} />
      </div>
      {!collapsed && (
        <div className="flex flex-col gap-1 mt-3">
          {data.items.map((item) => (
            <button
              key={item.key}
              onClick={() => { if (!item.done) navigate(item.href); }}
              disabled={item.done}
              className={`flex items-start gap-2.5 text-left text-xs rounded-lg px-1.5 py-1.5 transition-colors ${item.done ? "text-white/25 cursor-default" : "text-white/70 hover:text-white hover:bg-white/5"}`}
              data-testid={`finish-setup-${item.key}`}
            >
              {item.done
                ? <CheckCircle2 size={14} className="text-emerald-400 shrink-0 mt-px" />
                : <Circle size={14} className="text-white/25 shrink-0 mt-px" />}
              <span className="flex-1 min-w-0">
                <span className={item.done ? "line-through" : ""}>{item.label}</span>
                {!item.done && item.detail && <span className="block text-[11px] text-white/35 mt-0.5">{item.detail}</span>}
              </span>
              {!item.done && <ChevronRight size={12} className="text-white/25 shrink-0 mt-px" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
